import { useT } from '@/core/I18nProvider';

import type { ReactElement } from 'react';

/**
 * Skip-to-main-content link (Phase 7 v1.1 iter 298).
 *
 * WCAG 2.1 AA technique **G1** — "Adding a link at the top of each
 * page that goes directly to the main content area." Without this,
 * a keyboard or screen-reader user pressing Tab on the marketing
 * site lands on the first link in the header/nav and has to walk
 * past every navigation item before reaching the actual page
 * content on every route.
 *
 * **Mounted globally in `App.tsx`** so the link is the absolute
 * first focusable element on every route. The matching `id="main"`
 * lives on each page's `<main>` element (also given `tabIndex={-1}`
 * so the browser can focus it on the `#main` hash jump).
 *
 * **Visible-on-focus only** (Designer hat, locked): Tailwind's
 * `sr-only` keeps the link out of the visible layout for sighted
 * mouse users; `focus:not-sr-only` un-hides it the moment a
 * keyboard user tabs in. The on-focus styling pins the link to
 * the top-left corner via `focus:fixed` + `focus:left-*` +
 * `focus:top-*` so it doesn't displace any page content.
 *
 * **High z-index** (Designer hat, locked): `focus:z-50` lifts the
 * link above the iter-269 Footer + the iter-265 Hero on the home
 * route. The exact value matches the rest of the site's overlay
 * primitives.
 *
 * **Lowercase label** (Marketer hat, locked, iter-265 + iter-288 +
 * iter-295 + iter-297 CTA convention): "skip to main content" not
 * "Skip to Main Content". Browser tab titles use Title Case (per
 * iter-297 useDocumentTitle decisions); in-page CTAs stay
 * lowercase.
 *
 * **No JS** (Architect hat, locked): a plain anchor with an
 * in-page hash target. Browsers natively focus the element with
 * matching `id` on hash-jump (provided the target is interactive
 * or has `tabIndex`), so no `useEffect` / programmatic focus call
 * is needed. The hook ships as a pure component.
 */
export function SkipLink(): ReactElement {
  const { t } = useT();
  return (
    <a
      href="#main"
      className="sr-only focus:not-sr-only focus:fixed focus:start-2 focus:top-2 focus:z-50 focus:inline-flex focus:items-center focus:rounded-md focus:bg-foreground focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--bd-blue)] focus-visible:ring-offset-2"
    >
      {t('a11y.skipToMain')}
    </a>
  );
}
